import type {
  IntakeDraft,
  IntakeIssue,
  IntakeLinkKey,
  IntakeStopKey,
} from "../types/containerOrderIntake";

/**
 * Small helpers for editing an intake draft immutably by path.
 *
 * The review page edits a deep, server-shaped object — the container order,
 * its containers and their links — and addresses every field by the same dotted
 * path the server uses in its issues (`order.route.loadingSite.address.city`,
 * `lines.0.cargoItems.1.weightKg`). Using one path language for both is what
 * lets an issue be highlighted on the field it is about.
 */

const isIndex = (key: string) => /^\d+$/.test(key);

export function getIn(obj: unknown, path: string): any {
  return path.split(".").reduce<any>((node, key) => (node == null ? undefined : node[key]), obj);
}

/** A copy of `obj` with `value` at `path`; only the branch walked is copied. */
export function setIn<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const walk = (node: any, index: number): any => {
    const key = keys[index];
    const base = Array.isArray(node) ? [...node] : { ...(node ?? {}) };
    if (index === keys.length - 1) {
      base[key] = value;
      return base;
    }
    const child = node?.[key] ?? (isIndex(keys[index + 1]) ? [] : {});
    base[key] = walk(child, index + 1);
    return base;
  };
  return walk(obj, 0);
}

/** Where on the draft each block (the client or a stop) lives. */
export function linkBlockPath(draft: IntakeDraft, key: IntakeStopKey): string | null {
  switch (key) {
    case "client":
      return "order.client";
    case "loadingSite":
      return "order.route.loadingSite";
    case "terminalPickup":
      return "order.route.terminalPickup";
    case "terminalDropoff":
      return "order.route.terminalDropoff";
    case "unloading": {
      const index = draft.order.route.additionalStops.findIndex(
        (stop) => stop.intakeRole === "unloading",
      );
      return index >= 0 ? `order.route.additionalStops.${index}` : null;
    }
    default:
      return null;
  }
}

/**
 * Which id field a link fills on its block.
 *
 * Only the client is a partner. The loading site and both terminals are places,
 * picked from Addresses as on the order form.
 */
export function linkIdField(key: IntakeLinkKey): "partnerId" | "addressId" {
  return key === "client" ? "partnerId" : "addressId";
}

/** Issues about a path or anything beneath it. */
export function issuesAt(issues: IntakeIssue[], path: string): IntakeIssue[] {
  return issues.filter(
    (issue) => issue.path === path || issue.path.startsWith(`${path}.`),
  );
}

/** The first blocking message for exactly this field, for its error text. */
export function fieldError(issues: IntakeIssue[], path: string): string | undefined {
  return issues.find((issue) => issue.severity === "blocking" && issue.path === path)?.message;
}

/**
 * The blocking and the warning issue for exactly this field.
 *
 * Both, because they are shown differently and a field can carry one of each:
 * the blocking one is the control's error, the warning sits under it in amber.
 * Since the summary list above the form was removed, this — and the cards' own
 * tinted borders — is the only place either is read, so a warning that has no
 * field to land on is a warning nobody sees.
 */
export function fieldIssue(issues: IntakeIssue[], path: string) {
  const here = issues.filter((issue) => issue.path === path);
  return {
    blocking: here.find((issue) => issue.severity === "blocking"),
    warning: here.find((issue) => issue.severity === "warning"),
  };
}

export const hasCoordinates = (coordinates?: {
  latitude: number | null;
  longitude: number | null;
}) =>
  typeof coordinates?.latitude === "number" &&
  typeof coordinates?.longitude === "number";
